import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api, eur } from "@/lib/api";

export default function CartPage() {
  const { items, setQty, remove, total } = useCart();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const shipping = settings
    ? settings.free_shipping_over > 0 && total >= settings.free_shipping_over
      ? 0
      : settings.shipping_cost
    : null;

  return (
    <div data-testid="cart-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <div className="overline-tag">Il tuo ordine</div>
      <h1 className="mt-3 font-display font-medium uppercase tracking-tight text-4xl md:text-5xl">Carrello</h1>

      {items.length === 0 ? (
        <div data-testid="cart-page-empty" className="mt-12 border border-dashed border-[#e5e5e5] bg-white py-20 text-center">
          <p className="font-display text-2xl uppercase tracking-tight">Il carrello è vuoto</p>
          <Link data-testid="empty-cart-cta" to="/catalogo" className="btn-hard mt-6 inline-flex">
            Vai al catalogo
          </Link>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 flex flex-col divide-y divide-[#e5e5e5] border-y border-[#e5e5e5]">
            {items.map((i) => (
              <div key={i.id} data-testid={`cart-row-${i.id}`} className="py-6 flex gap-5 items-center">
                <img src={i.image} alt={i.name} className="w-24 h-24 object-cover border border-[#e5e5e5]" />
                <div className="flex-1">
                  <Link to={`/prodotto/${i.id}`} className="font-semibold hover:text-[#ff4500] transition-colors">{i.name}</Link>
                  <div className="text-sm text-muted-foreground mt-1">{eur(i.price)} cad.</div>
                  <div className="mt-3 flex items-center gap-2">
                    <button data-testid={`cart-qty-minus-${i.id}`} onClick={() => setQty(i.id, i.quantity - 1)} className="border border-[#e5e5e5] p-1.5 hover:border-[#0a0a0a] transition-colors" aria-label="Diminuisci">
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="font-mono text-sm w-8 text-center">{i.quantity}</span>
                    <button data-testid={`cart-qty-plus-${i.id}`} onClick={() => setQty(i.id, i.quantity + 1)} className="border border-[#e5e5e5] p-1.5 hover:border-[#0a0a0a] transition-colors" aria-label="Aumenta">
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display font-semibold text-lg">{eur(i.price * i.quantity)}</div>
                  <button data-testid={`cart-remove-${i.id}`} onClick={() => remove(i.id)} className="mt-2 text-muted-foreground hover:text-[#ff4500] transition-colors" aria-label="Rimuovi">
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="lg:col-span-4">
            <div className="bg-[#062e20] text-white p-7 lg:sticky lg:top-40">
              <h2 className="font-display font-medium text-xl uppercase tracking-tight">Riepilogo</h2>
              <div className="mt-5 flex justify-between text-sm text-white/70">
                <span>Subtotale</span>
                <span>{eur(total)}</span>
              </div>
              <div className="mt-2 flex justify-between text-sm text-white/70">
                <span>Spedizione</span>
                <span data-testid="cart-shipping">
                  {shipping === null ? "..." : shipping === 0 ? "Gratuita" : `${eur(shipping)}${settings?.free_shipping_over > 0 ? ` · gratis sopra ${eur(settings.free_shipping_over)}` : ""}`}
                </span>
              </div>
              <div className="mt-5 pt-5 border-t border-white/15 flex justify-between font-display font-semibold text-2xl">
                <span>Totale</span>
                <span data-testid="cart-page-total" className="text-[#ffd600]">{eur(total)}</span>
              </div>
              <Link data-testid="cart-checkout-button" to="/checkout" className="btn-hard w-full mt-6 !shadow-[4px_4px_0px_#ffd600]">
                Procedi al checkout <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </Link>
              <p className="mt-4 text-xs text-white/50 text-center">Pagamento in contrassegno alla consegna</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
